import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Doc, Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { ArrowLeft, AlertCircle, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/layout";
import { ProcessingSpinner } from "@/components/processing-spinner";
import { ConfidenceIndicator } from "@/components/confidence-indicator";
import { EditableDataGrid } from "@/components/editable-data-grid";
import { SpecPilotSourcePdfPanel } from "@/components/spec-pilot-source-pdf-panel";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getTableRowsPayload, type TableRowsPayload, type TableRowsPayloadRef } from "@/lib/table-rows-cache";
import { uploadTableRowsPayload } from "@/lib/table-rows-upload";
import { ActionLogProvider, useRecordActionLog } from "@/hooks/use-action-log-session";

// ============================================================================
// Route Definition
// ============================================================================

export const Route = createFileRoute("/specPilot/$jobId_/tables/$tableId")({
  component: TableDetailPage,
});

// ============================================================================
// Page Component
// ============================================================================

function TableDetailPage() {
  const { jobId, tableId } = Route.useParams();
  const { data: job } = useQuery({
    ...convexQuery(api.specPilot.submittals.get, {
      submittalId: jobId as Id<"specPilotSubmittals">,
    }),
    throwOnError: true,
  });

  return (
    <ActionLogProvider
      projectId={job?.projectId ?? null}
      pilot="SpecPilot"
      targetKind="specPilotSubmittal"
      targetId={jobId}
    >
      <TableDetailContent job={job} jobId={jobId} tableId={tableId} />
    </ActionLogProvider>
  );
}

function TableDetailContent({
  job,
  jobId,
  tableId,
}: {
  job?: Doc<"specPilotSubmittals"> | null;
  jobId: string;
  tableId: string;
}) {

  // Fetch table and job data
  const { data: table } = useQuery(convexQuery(api.specPilot.extractedTables.get, {
    tableId: tableId as Id<"specPilotExtractedTables">,
  }));
  const { data: rowPayloadRef } = useQuery(
    convexQuery(
      api.specPilot.extractedTables.getRowsPayloadRef,
      {
        tableId: tableId as Id<"specPilotExtractedTables">,
      }
    )
  );
  const recordActionLog = useRecordActionLog();
  const { data: sourcePdf } = useQuery(
    convexQuery(
      api.specPilot.submittals.getSourcePdfUrl,
      job ? { submittalId: jobId as Id<"specPilotSubmittals"> } : "skip"
    )
  );
  const [rowPayload, setRowPayload] = useState<TableRowsPayload | null>(null);
  const [rowPayloadError, setRowPayloadError] = useState<string | null>(null);
  const [isHydratingRows, setIsHydratingRows] = useState(false);

  // Mutation for updating table data
  const updateTableData = useMutation(api.specPilot.extractedTables.updateData);
  const generateR2UploadUrl = useMutation(api.files.generateR2UploadUrl);

  // Handle save
  const handleSave = async (
    headers: string[],
    rows: string[][],
    metadata?: { deletedRowIndices: number[] }
  ) => {
    try {
      const uploadedRows = await uploadTableRowsPayload({
        generateUploadUrl: async () =>
          await generateR2UploadUrl({
            prefix: "spec-pilot/rows",
            filename: `${tableId}-rows.json.gz`,
          }),
        headers,
        rows,
      });

      await updateTableData({
        tableId: tableId as Id<"specPilotExtractedTables">,
        headers,
        rowsStorageKey: uploadedRows.rowsStorageKey,
        rowsFormat: uploadedRows.rowsFormat,
        rowCount: uploadedRows.rowCount,
        deletedRowIndices: metadata?.deletedRowIndices ?? [],
      });
      await recordActionLog();
      toast.success("Table saved successfully");
    } catch (error) {
      toast.error("Failed to save table");
      console.error("Error saving table:", error);
      throw error; // Re-throw to let EditableDataGrid handle the error state
    }
  };

  useEffect(() => {
    let cancelled = false;
    setRowPayload(null);

    if (rowPayloadRef === undefined) return () => {
      cancelled = true;
    };

    if (rowPayloadRef === null) {
      setRowPayload(null);
      setRowPayloadError("Table rows could not be loaded.");
      return () => {
        cancelled = true;
      };
    }

    async function hydrateRows() {
      setIsHydratingRows(true);
      setRowPayloadError(null);
      try {
        const payload = await getTableRowsPayload(
          rowPayloadRef as TableRowsPayloadRef
        );
        if (!cancelled) {
          setRowPayload(payload);
        }
      } catch (error) {
        console.error("Failed to load table rows", error);
        if (!cancelled) {
          setRowPayload(null);
          setRowPayloadError("Failed to load table rows.");
        }
      } finally {
        if (!cancelled) {
          setIsHydratingRows(false);
        }
      }
    }

    void hydrateRows();
    return () => {
      cancelled = true;
    };
  }, [rowPayloadRef]);

  const hydratedTable = useMemo(() => {
    if (!table || !rowPayload) return null;
    return {
      ...table,
      headers: rowPayload.headers,
      rows: rowPayload.rows,
    };
  }, [table, rowPayload]);

  // Loading state
  if (
    table === undefined ||
    job === undefined ||
    rowPayloadRef === undefined ||
    (table !== null && !rowPayload && !rowPayloadError && isHydratingRows)
  ) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Loading..."
          breadcrumbs={[
            { label: "SpecPilot", href: "/specPilot" },
            { label: "Loading..." },
          ]}
        />
        <Card>
          <ProcessingSpinner message="Loading table data..." />
        </Card>
      </div>
    );
  }

  // Table not found state
  if (table === null) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Table Not Found"
          breadcrumbs={[
            { label: "SpecPilot", href: "/specPilot" },
            { label: job?.name ?? "Job", href: `/specPilot/${jobId}` },
            { label: "Not Found" },
          ]}
        />
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Table not found</AlertTitle>
          <AlertDescription>
            The table you are looking for does not exist or has been deleted.
          </AlertDescription>
        </Alert>
        <Button asChild variant="outline">
          <Link to="/specPilot/$jobId" params={{ jobId }}>
            <ArrowLeft className="size-4" />
            Back to Job
          </Link>
        </Button>
      </div>
    );
  }

  // Job not found state (table exists but job doesn't - orphaned data)
  if (job === null) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Job Not Found"
          breadcrumbs={[
            { label: "SpecPilot", href: "/specPilot" },
            { label: "Not Found" },
          ]}
        />
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Job not found</AlertTitle>
          <AlertDescription>
            The job associated with this table does not exist or has been
            deleted.
          </AlertDescription>
        </Alert>
        <Button asChild variant="outline">
          <Link to="/specPilot">
            <ArrowLeft className="size-4" />
            Back to SpecPilot
          </Link>
        </Button>
      </div>
    );
  }

  const tableName = table.name || "Untitled Table";
  const rowCount = hydratedTable?.rows.length ?? table.rowCount ?? 0;
  const colCount = hydratedTable?.headers.length ?? table.headerCount ?? 0;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title={tableName}
        breadcrumbs={[
          { label: "SpecPilot", href: "/specPilot" },
          { label: job.name, href: `/specPilot/${jobId}` },
          { label: tableName },
        ]}
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/specPilot/$jobId" params={{ jobId }}>
              <ArrowLeft className="size-4" />
              Back to Job
            </Link>
          </Button>
        }
      />

      {/* Table Metadata Card */}
      <Card className="py-4">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileSpreadsheet className="size-4" />
            Table Information
          </CardTitle>
          <CardDescription>
            Metadata and extraction details for this table
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Table Name */}
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Name</p>
              <p className="text-sm font-medium">{tableName}</p>
            </div>

            {/* Page Number */}
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">
                Page Number
              </p>
              <p className="text-sm font-medium">Page {table.pageNumber}</p>
            </div>

            {/* Dimensions */}
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">
                Dimensions
              </p>
              <p className="text-sm font-medium">
                {rowCount} rows x {colCount} columns
              </p>
            </div>

            {/* Confidence Score */}
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">
                Confidence
              </p>
              {table.confidence !== undefined ? (
                <ConfidenceIndicator
                  confidence={table.confidence}
                  variant="badge"
                  showLabel
                />
              ) : (
                <Badge variant="outline" className="text-muted-foreground">
                  Not available
                </Badge>
              )}
            </div>
          </div>

          {/* Additional metadata row */}
          <div className="mt-4 flex flex-wrap items-center gap-4 border-t pt-4">
            {/* Extraction Method */}
            {table.extractionMethod && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  Extraction Method:
                </span>
                <Badge variant="secondary" className="text-xs">
                  {table.extractionMethod === "pdfplumber"
                    ? "PDF Plumber"
                    : "Vision AI"}
                </Badge>
              </div>
            )}

            {/* Selection Status */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                Submittal Status:
              </span>
              <Badge
                variant={table.selectedForSubmittal ? "default" : "outline"}
                className="text-xs"
              >
                {table.selectedForSubmittal
                  ? "Selected for Submittal"
                  : "Not Selected"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,42%)]">
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle className="text-base">Table Data</CardTitle>
            <CardDescription>
              Click any cell to edit. Changes are saved when you click the Save
              button.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {rowPayloadError ? (
              <Alert variant="destructive">
                <AlertCircle className="size-4" />
                <AlertTitle>Unable to load rows</AlertTitle>
                <AlertDescription>{rowPayloadError}</AlertDescription>
              </Alert>
            ) : hydratedTable ? (
              <EditableDataGrid
                headers={hydratedTable.headers}
                rows={hydratedTable.rows}
                onSave={handleSave}
              />
            ) : (
              <ProcessingSpinner message="Loading table rows..." />
            )}
          </CardContent>
        </Card>

        <Card className="py-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Source PDF</CardTitle>
            <CardDescription>
              Showing page {table.pageNumber} for the active table.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SpecPilotSourcePdfPanel
              sourcePdf={sourcePdf}
              sourceId={jobId}
              pageNumber={table.pageNumber}
              className="h-[70vh] min-h-[480px] lg:h-[calc(100vh-360px)] lg:min-h-[560px]"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/*
 * Usage Example:
 *
 * The table detail page is automatically rendered when navigating to
 * /specPilot/:jobId/tables/:tableId. It provides inline editing capabilities
 * for the extracted table data.
 *
 * Example navigation:
 * <Link to="/specPilot/$jobId/tables/$tableId" params={{ jobId: "123", tableId: "456" }}>
 *   View Table
 * </Link>
 *
 * Accessibility Checklist:
 * - Breadcrumb navigation with proper ARIA labels
 * - All interactive elements are keyboard accessible
 * - Status changes announced via toast notifications
 * - Proper heading hierarchy maintained
 * - Color contrast meets WCAG requirements
 *
 * Performance Considerations:
 * - Uses Convex real-time subscriptions for automatic updates
 * - EditableDataGrid manages local state to minimize re-renders
 * - Lazy loading of table data on route entry
 * - Save operations are batched (not per-keystroke)
 */
